import validate from '@saucelabs/sl-util-validate';
import { constants as tunnelConstants } from '@saucelabs/sl-module-tunnels';

import * as globalConstants from '../../constants';

// TODO: get these in the duck 🦆
const {
  CONFIGURATION_TEST_TYPES: { APP, WEB },
  CONFIGURATION_DEVICE_TYPES: { DESKTOP_BROWSER, REAL_DEVICE, VIRTUAL_DEVICE },
  CONFIGURATION_COMBINATION_TYPES: {
    WEB_DESKTOP_BROWSER,
    WEB_REAL_DEVICE,
    WEB_VIRTUAL_DEVICE,
    APP_REAL_DEVICE,
    APP_VIRTUAL_DEVICE,
  },
  TEST_CONFIGURATION_TABS,
} = globalConstants;

/**
 * Determines what kind of manual test the user will run
 * @param {*} testType - 'APP' or 'WEB'
 * @param {*} deviceType - 'DESKTOP_BROWSER' or 'REAL_DEVICE' or 'VIRTUAL_DEVICE'
 * @returns {string|null} - the kind of manual test
 */
const getConfigurationCombinationType = (testType, deviceType) => {
  if (testType === WEB && deviceType === DESKTOP_BROWSER) {
    return WEB_DESKTOP_BROWSER;
  }
  if (testType === WEB && deviceType === REAL_DEVICE) {
    return WEB_REAL_DEVICE;
  }
  if (testType === WEB && deviceType === VIRTUAL_DEVICE) {
    return WEB_VIRTUAL_DEVICE;
  }
  if (testType === APP && deviceType === REAL_DEVICE) {
    return APP_REAL_DEVICE;
  }
  if (testType === APP && deviceType === VIRTUAL_DEVICE) {
    return APP_VIRTUAL_DEVICE;
  }
  return null;
};

const constraints = {
  [globalConstants.CONFIGURATION_COMBINATION_TYPES.WEB_DESKTOP_BROWSER]: {
    browserConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [globalConstants.CONFIGURATION_COMBINATION_TYPES.WEB_REAL_DEVICE]: {
    realDeviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [globalConstants.CONFIGURATION_COMBINATION_TYPES.WEB_VIRTUAL_DEVICE]: {
    virtualDeviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [globalConstants.CONFIGURATION_COMBINATION_TYPES.APP_REAL_DEVICE]: {
    mobileAppConfiguration: {
      presence: { allowEmpty: false },
    },
    realDeviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
  [globalConstants.CONFIGURATION_COMBINATION_TYPES.APP_VIRTUAL_DEVICE]: {
    mobileAppConfiguration: {
      presence: { allowEmpty: false },
    },
    virtualDeviceConfiguration: {
      presence: { allowEmpty: false },
    },
  },
};

/**
 * Validates the "form data" for starting a session.
 * @param {object} testConfig
 * @param {string} testConfig.deviceType - 'DESKTOP_BROWSER' or 'REAL_DEVICE' or 'VIRTUAL_DEVICE'
 * @param {AppStorageFile} testConfig.mobileAppConfiguration - mobile app to be tested
 * @param {browserConfiguration} testConfig.browserConfiguration - web app to be tested
 * @param {deviceRecords.DeviceDescriptor} testConfig.realDeviceConfiguration - real device to test
 * @param {virtualDeviceConfiguration} testConfig.virtualDeviceConfiguration
 *   - web app to be tested on virtual device
 * @param {string} testConfig.testType - 'WEB' or 'APP'
 * @returns {object|undefined} key is the field, value is string of error
 *   example: { mobileAppConfiguration: "Mobile app configuration can't be blank" }
 */
const getValidationErrors = ({
  browserConfiguration,
  deviceType,
  mobileAppConfiguration,
  realDeviceConfiguration,
  testType,
  virtualDeviceConfiguration,
}) => {
  const configurationCombinationType = getConfigurationCombinationType(testType, deviceType);

  if (!configurationCombinationType) {
    return {};
  }

  return validate({
    browserConfiguration,
    mobileAppConfiguration,
    realDeviceConfiguration,
    virtualDeviceConfiguration,
  }, constraints[configurationCombinationType]);
};

/**
* Appends 'http://' to the beginning of a URL if it lacks ':/' and isn't blank
* @param {string} url
* @returns {string} url with 'http://' appended if ':/' not present
*/
const normalizeUrl = (url = '') => {
  const urlWithoutWhitespace = `${url}`.replace(/\s+/g, '');
  if (urlWithoutWhitespace === '' || url.includes(':/')) {
    return urlWithoutWhitespace;
  }

  return `http://${urlWithoutWhitespace}`;
};

/**
 * Returns an array of tab keys to be displayed on the web/live testing dashboard
 * @param {object} tabs Possible config of tabs to be displayed based on diff. scenarios for iOS
 * @param {ImmutableMap} appConfiguration MobileAppConfiguration Record, only used for app testing
 * @returns {array} array of tab keys to be displayed
 */
const getTabs = (tabs, appConfiguration) => {
  if (tabs?.DEFAULT) {
    return tabs.DEFAULT;
  }

  if (!tabs || !appConfiguration) {
    return [];
  }

  const os = appConfiguration.kind.toUpperCase();

  // Only used for iOS to check if the buildType is for virtual or real device
  const buildType = appConfiguration.metadata?.is_simulator ? 'VIRTUAL' : 'REAL';

  return tabs[os] || tabs[`${os}_${buildType}`] || [];
};

/**
 * Returns the currently displayed tab key within the config form
 * which is either the activly selected tab or the default tab from a recent config
 * @param {string} activeTab one of constants.TEST_CONFIGURATION_TABS
 * @param {string} deviceType one of constants.DEVICE_TYPE
 * @returns {string} one of constants.TEST_CONFIGURATION_TABS
 */
const getActiveTabKey = (activeTab = '', deviceType = '') => {
  const tab = ({
    [DESKTOP_BROWSER]: TEST_CONFIGURATION_TABS.DESKTOP,
    [REAL_DEVICE]: TEST_CONFIGURATION_TABS.MOBILE_REAL,
    [VIRTUAL_DEVICE]: TEST_CONFIGURATION_TABS.MOBILE_VIRTUAL,
  })[deviceType] || deviceType;

  return activeTab || tab;
};

/**
 * Returns whether a tunnel is still running.
 * @param {string} tunnelUnderTest - name of the selected tunnel from the dropdown
 * @param {Immutable.List<TunnelRecord>>} tunnels
 * @returns {boolean} true if tunnel tunnelUnderTest still running, false otherwise
 */
const isTunnelStillRunning = (tunnelUnderTest, tunnels) => tunnels.some((tunnel) => (
  tunnel.getIdentifier() === tunnelUnderTest && tunnel.isRunning()),
);

/**
 * Returns whether a tunnel is still running.
 * @param {Immutable.List<TunnelRecord>>} tunnels
 * @returns {Object[]} see tunnelOptions below for type of Object (has key, text, value, disabled)
 */
const prepTunnelOptions = (tunnels) => {
  if (tunnels.size === 0) {
    return [];
  }

  const tunnelOptions = tunnels.map((item) => ({
    key: item.get('id'),
    text: item.getName(),
    value: item.getIdentifier(),
    disabled: item.get('status') !== tunnelConstants.TUNNEL_STATUS.RUNNING,
  }));

  return tunnelOptions.unshift({
    key: 'none',
    text: 'None',
    value: '-1',
    disabled: false,
  }).toJS();
};

const idKeyString = 'tunnel-identifier:';
/**
 * Parses the string created by getIdentifier() method for a TunnelRecord
 * "tunnel" can be of format 'OWNER' or 'tunnel-identifier:TUNNEL_IDENTIFIER,OWNER'
 * If no TUNNEL_IDENTIFIER exists, OWNER is the identifier
 * In the store TUNNEL_IDENTIFIER is the property 'tunnel_identifier' (for each TunnelRecord)
 * In the store OWNER is the property 'owner' (for each TunnelRecord)
 * Returns an object with the following keys:
 * tunnelName (= tunnel_identifier in TunnelRecord)
 * tunnelOwner (= owner in TunnelRecord)
 * @param {string} tunnel
 * @returns {Object} tunnelNames - parsed values extracted from formData.tunnel
 * @returns {string} tunnelNames.tunnelName - owner or tunnel_identifier
 * @returns {string} tunnelNames.tunnelOwner - owner
 */
const parseTunnelNameString = (tunnel) => {
  const tunnelNames = {
    tunnelName: null,
    tunnelOwner: null,
  };

  if (typeof tunnel !== 'string' || tunnel.length === 0) {
    return tunnelNames;
  }

  if (tunnel.startsWith(idKeyString)) {
    const tunnelNamesArray = tunnel.slice(idKeyString.length).split(',');
    tunnelNames.tunnelName = tunnelNamesArray[0];
    tunnelNames.tunnelOwner = tunnelNamesArray[1];
  } else {
    tunnelNames.tunnelOwner = tunnel;
  }
  return tunnelNames;
};

export default {
  getActiveTabKey,
  getConfigurationCombinationType,
  getTabs,
  getValidationErrors,
  isTunnelStillRunning,
  normalizeUrl,
  parseTunnelNameString,
  prepTunnelOptions,
};
